<template>
  <div class="w-full h-full">
    <AlertDialog :open="showFormModal" @update:open="(o) => !o && (showFormModal = false)">
      <AlertDialogContent class="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>{{ selectedRetailer && selectedRetailer.retailerName }}</AlertDialogTitle>
          <AlertDialogDescription>Select users who spend at this retailer.</AlertDialogDescription>
        </AlertDialogHeader>
        <div class="space-y-3">
          <div>
            <p class="text-xs font-semibold text-muted-foreground pb-1">Example:</p>
            <div class="rounded-lg py-3 px-3 bg-muted border border-border text-sm">
              <p class="leading-6">
                Select users that have spent
                <span class="border bg-white rounded px-1.5 p-1">CONDITION</span>
                <span class="text-nowrap"
                  >£ <span class="border bg-white rounded px-1.5 p-1">AMOUNT</span></span
                >
                at {{ selectedRetailer && selectedRetailer.retailerName }}
              </p>
            </div>
          </div>
          <div class="border-t pt-3 grid grid-cols-1 gap-2">
            <p class="text-xs font-semibold text-muted-foreground">Enter your variables:</p>
            <div>
              <label class="text-sm font-medium" for="arr-condition">Condition</label>
              <Select v-model="addRetailerRule.condition">
                <SelectTrigger id="arr-condition" class="w-full">
                  <SelectValue placeholder="Select condition" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="o in rewardType" :key="String(o.id)" :value="o.id">{{ o.text }}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label class="text-sm font-medium" for="arr-amount">Amount (£)</label>
              <Input id="arr-amount" v-model="addRetailerRule.amount" placeholder="100" type="number" />
            </div>
            <p
              v-if="addRetailerRule.condition != undefined && addRetailerRule.amount != undefined"
              class="text-xs font-semibold text-muted-foreground pt-3 mb-0"
            >
              Your retailer rule:
            </p>
            <div
              v-if="addRetailerRule.condition != undefined && addRetailerRule.amount != undefined"
              class="rounded-lg py-3 px-3 bg-muted border border-border text-sm"
            >
              <p class="leading-6">
                Select users that have spent
                <span class="border bg-white rounded px-1.5 p-1">{{
                  getConditionText(addRetailerRule.condition)
                }}</span>
                <span class="text-nowrap"
                  >£
                  <span class="border bg-white rounded px-1.5 p-1">{{
                    addRetailerRule.amount
                  }}</span></span
                >
                at {{ selectedRetailer && selectedRetailer.retailerName }}
              </p>
            </div>
          </div>
        </div>
        <AlertDialogFooter class="gap-2">
          <AlertDialogCancel @click="showFormModal = false">Cancel</AlertDialogCancel>
          <AlertDialogAction
            :disabled="!(addRetailerRule.condition != undefined && addRetailerRule.amount != undefined)"
            @click="submit()"
          >Add rule</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <div v-if="!embedded" class="flex items-center gap-3 mb-4">
      <Plus class="w-7 h-7 text-foreground" />
      <h1 class="text-2xl font-semibold tracking-tight">Create a new audience</h1>
    </div>

    <Card class="mb-4">
      <CardHeader>
        <CardTitle>Name & timeframe</CardTitle>
        <CardDescription>Give your audience a name and choose the analysis window.</CardDescription>
      </CardHeader>
      <CardContent>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div class="grid gap-1.5">
            <label class="text-sm font-medium" for="ao-name">Audience name</label>
            <Input id="ao-name" v-model="audienceObject.name" placeholder="Describe this audience" />
          </div>
          <div class="grid gap-1.5">
            <label class="text-sm font-medium" for="ao-period">Rolling analysis timeframe</label>
            <Select v-model="periodStr">
              <SelectTrigger id="ao-period" class="w-full">
                <SelectValue placeholder="Select timeframe" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="o in periodSelect" :key="String(o.id)" :value="String(o.id)">{{ o.text }}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card class="mb-4">
      <CardHeader>
        <CardTitle>Segment by overall spend volume</CardTitle>
        <CardDescription>Target users based on their total spend across all retailers.</CardDescription>
        <CardAction>
          <Button
            size="sm"
            variant="outline"
            @click="audienceObject.demographicConditions.push({ ...addDemographicData })"
          >
            <Plus class="w-4 h-4" />Add rule
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <div
          v-if="audienceObject.demographicConditions.length === 0"
          class="rounded-lg border border-dashed py-8 text-center text-sm text-muted-foreground"
        >
          No spend rules yet. Add a rule to get started.
        </div>
        <div v-else class="grid gap-3">
          <div
            v-for="(val, index) in audienceObject.demographicConditions"
            :key="index"
            class="rounded-lg border bg-muted/40 p-3"
          >
            <div class="flex items-center justify-between">
              <p class="text-sm font-medium">Rule {{ index + 1 }}</p>
              <Button
                size="sm"
                variant="ghost"
                class="text-muted-foreground hover:text-destructive"
                @click="audienceObject.demographicConditions.splice(index, 1)"
              >
                <Trash2 class="w-4 h-4" />Remove
              </Button>
            </div>
            <div class="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <span class="text-muted-foreground">Users that have spent</span>
              <Select v-model="audienceObject.demographicConditions[index].condition">
                <SelectTrigger class="w-full">
                  <SelectValue placeholder="Select condition" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="o in rewardType" :key="String(o.id)" :value="o.id">{{ o.text }}</SelectItem>
                </SelectContent>
              </Select>
              <div class="relative">
                <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                <Input
                  v-model="audienceObject.demographicConditions[index].amount"
                  placeholder="100"
                  class="w-32 pl-6"
                  type="number"
                />
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card class="mb-4">
      <CardHeader>
        <CardTitle>Segment by retailer</CardTitle>
        <CardDescription>Target users based on their spend at specific retailers.</CardDescription>
        <CardAction>
          <Button
            size="sm"
            variant="outline"
            v-if="audienceObject.retailerSegments.length == 0"
            @click="audienceObject.retailerSegments.push(addRetailerSegment)"
          >
            <Plus class="w-4 h-4" />Add rule
          </Button>
          <Button
            size="sm"
            variant="ghost"
            class="text-muted-foreground hover:text-destructive"
            v-if="audienceObject.retailerSegments.length > 0"
            @click="audienceObject.retailerSegments.pop()"
          >
            <Trash2 class="w-4 h-4" />Remove
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <div v-for="(retailerSegment, index) in audienceObject.retailerSegments" :key="index">
          <div class="relative">
            <Search class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              v-model="retailerSearchInput"
              placeholder="Search by retailer name"
              class="pl-9"
              @input="onRetailerSearchInput"
            />
          </div>
          <div v-if="showResultsTable" class="mt-4 rounded-lg border overflow-hidden">
          <DataTable
            :headers="headersTable1"
            :items="retailers"
            :loading="loading"
            :error="error"
            @retry="getRetailerBySearch(serverOptions.page - 1, serverOptions.rowsPerPage, retailerSearchInput)"
            v-model:server-options="serverOptions"
            :server-items-length="serverItemsLength"
            :hide-footer="true"
          >
            <!-- Retailer Name -->
            <template #item-retailer="retailers">
              <div class="flex items-center gap-2.5">
                <img v-if="retailers.logo" :src="retailers.logo" class="w-8 h-8 rounded-md border border-border object-contain shrink-0 bg-white" alt="" />
                <span class="text-sm font-medium truncate" :title="retailers.retailerName">{{ retailers.retailerName }}</span>
              </div>
            </template>

            <!-- Retailer Website -->
            <template #item-website="retailers">
              <span v-if="retailers.retailerWebsite" class="text-xs text-muted-foreground truncate" :title="retailers.retailerWebsite">{{ retailers.retailerWebsite }}</span>
              <span v-else class="text-muted-foreground">—</span>
            </template>

            <!-- Action Buttons -->
            <template #item-action="retailers">
              <div class="flex justify-end items-center gap-1">
                <Button
                  size="sm"
                  @click="
                    () => {
                      showFormModal = true
                      selectedRetailer = retailers
                      addRetailerRule.retailerWebsite = retailers.retailerWebsite
                      addRetailerRule.retailerName = retailers.retailerName
                      addRetailerRule.logo = retailers.logo
                    }
                  "
                >
                  <Plus class="w-4 h-4" />Add retailer
                </Button>
              </div>
            </template>
          </DataTable>
          </div>

          <div v-if="audienceObject.retailerSegments.length > 0" class="mt-4">
            <p
              v-if="
                audienceObject.retailerSegments.length > 0 &&
                audienceObject.retailerSegments[0].retailerConditions.length > 0
              "
              class="text-sm font-medium mb-2"
            >
              Retailer rules:
            </p>
            <div
              v-for="(rule, index) in audienceObject.retailerSegments[0].retailerConditions"
              :key="index"
              class="rounded-lg border bg-muted/40 px-3 py-2.5 mb-2"
            >
              <div class="flex justify-between items-center">
                <div class="flex items-center gap-2 text-sm min-w-0">
                  <img v-if="rule.logo" :src="rule.logo" class="w-6 h-6 rounded object-contain border bg-white shrink-0" alt="" />
                  <span class="text-sm font-medium truncate">{{ rule.retailerName || rule.retailerWebsite }}</span>
                </div>
                <Button
                  id="removeRetailerRule"
                  size="sm"
                  variant="ghost"
                  class="text-muted-foreground hover:text-destructive shrink-0"
                  @click="audienceObject.retailerSegments[0].retailerConditions.splice(index, 1)"
                >
                  <Trash2 class="w-4 h-4" />Remove
                </Button>
              </div>
              <div class="text-sm text-muted-foreground mt-1">
                <div>
                  Users that have spent
                  <span class="font-medium text-foreground">{{ getConditionText(rule.condition) }} </span>
                  <span class="font-medium text-foreground">£{{ rule.amount }}</span> at
                  <span class="font-medium text-foreground">{{ rule.retailerName || rule.retailerWebsite }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <Button
      :disabled="loading || !validForm || !validateDemographics || !validateRetailers"
      class="w-full"
      id="createContract"
      @click="createAudience()"
    >
      <WandSparkles class="w-4 h-4" />{{ isEditing ? 'Save changes' : 'Create audience' }}
    </Button>
  </div>
</template>

<script setup></script>

<script>
import { DateTime } from 'luxon'
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardAction,
  Input,
  Button,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '@boogi/ui'
import { Plus, Trash2, Search, WandSparkles } from 'lucide-vue-next'
import { DataTable } from '@boogi/ui'

export default {
  name: 'manage-publsihers',
  components: {
    DataTable,
    Card, CardHeader, CardTitle, CardDescription, CardContent, CardAction,
    Input,
    Button,
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
    Plus, Trash2, Search, WandSparkles,
  },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    audience: {
      type: Object,
      default: null,
    },
  },
  emits: ['created'],

  data() {
    return {
      retailerSearchInput: '',
      searchDebounce: null,
      serverOptions: {
        page: 1,
        rowsPerPage: 50,
      },

      selectedRetailer: undefined,
      showResultsTable: false,

      rewardType: [
        { id: 'MORE_THAN', text: 'more than' },
        { id: 'LESS_THAN', text: 'less than' },
      ],

      periodSelect: [
        { id: 30, text: '30 days' },
        { id: 60, text: '60 days' },
        { id: 90, text: '90 days' },
      ],

      addDemographicData: {
        condition: 'MORE_THAN',
        amount: undefined,
      },

      addRetailerRule: {
        retailerWebsite: '',
        retailerName: '',
        logo: '',
        condition: 'MORE_THAN',
        amount: undefined,
      },

      addRetailerSegment: {
        operand: 'AND',
        retailerConditions: [],
      },

      audienceObject: {
        name: '',
        period: 30,
        demographicConditions: [],
        retailerSegments: [],
      },

      retailerSearch: [],
      categorySOW: [],
      serverItemsLength: undefined,
      startDate: '',
      endDate: '',
      formStart: '',
      formEnd: '',
      category: '',

      showErrorModal: false,
      showSuccessModal: false,
      showFormModal: false,
      loading: false,
      error: false,
      headersTable1: [
        { text: 'Retailer', value: 'retailer' },
        { text: 'Website', value: 'website' },
        { text: 'Action', value: 'action' },
      ],

      headersTable2: [
        { text: 'Retailer', value: 'retailerWebsite' },
        { text: 'Condition', value: 'condition' },
        { text: 'Amount', value: 'amount' },
        { text: 'Rolling number of days', value: 'period' },
        { text: 'Action', value: 'action' },
      ],
      retailers: [],
    }
  },
  created() {
    const now = DateTime.local()
    const startOfMonth = now.startOf('month')
    const endOfMonth = now.endOf('month')
    this.startDate = startOfMonth.minus({ month: 2 })
    this.endDate = endOfMonth
    this.formStart = this.startDate.toFormat('dd-MM-yyyy')
    this.formEnd = this.endDate.toFormat('dd-MM-yyyy')

    if (this.audience) {
      this.audienceObject = {
        name: this.audience.name || '',
        period: this.audience.period || 30,
        demographicConditions: (this.audience.demographicConditions || []).map((c) => ({ ...c })),
        retailerSegments: (this.audience.retailerSegments || []).map((s) => ({
          operand: s.operand || 'AND',
          retailerConditions: (s.retailerConditions || []).map((rc) => ({ ...rc })),
        })),
      }
    }
  },
  methods: {
    onRetailerSearchInput() {
      clearTimeout(this.searchDebounce)
      const term = this.retailerSearchInput
      if (!term || term.trim() === '') {
        this.retailers = []
        this.showResultsTable = false
        return
      }
      this.searchDebounce = setTimeout(() => {
        this.getRetailerBySearch(0, 50, term)
        this.showResultsTable = true
      }, 300)
    },

    submit() {
      // Push a copy of the rule to avoid reference issues
      this.audienceObject.retailerSegments[0].retailerConditions.push({ ...this.addRetailerRule })
      this.showFormModal = false
      this.retailers = []
      this.showResultsTable = false
      this.selectedRetailer = undefined
      // Reset addRetailerRule
      this.addRetailerRule = {
        retailerWebsite: '',
        retailerName: '',
        logo: '',
        condition: 'MORE_THAN',
        amount: undefined,
      }
    },

    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATE_SHORT)
    },

    updateAnalytics() {
      this.startDate = DateTime.fromISO(this.formStart).startOf('day')
      this.endDate = DateTime.fromISO(this.formEnd).endOf('day')
      this.formStart = this.startDate.toFormat('dd-MM-yyyy')
      this.formEnd = this.endDate.toFormat('dd-MM-yyyy')
      this.getRetailers(0, 50)
      this.getCategories()
    },

    async createAudience() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        var body = { ...this.audienceObject }

        if (this.audienceObject.retailerSegments.length > 0) {
          body.retailerSegments[0].retailerConditions.forEach((element) => {
            delete element.retailerName
            delete element.logo
          })
        }

        const isEditing = this.isEditing
        const url = isEditing
          ? `${apiUrl}/private/api/client/audience/${this.audience.id}`
          : `${apiUrl}/private/api/client/audience`

        const response = await this.$apiFetch(url, {
          method: isEditing ? 'PUT' : 'POST',
          body: JSON.stringify(body),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          if (this.embedded) {
            this.$emit('created')
          } else {
            this.$router.push('/audience')
          }
        }
      } catch (error) {
        console.error(error)
        return
      }
    },

    async getRetailerBySearch(page, size, search) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false
      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/retailer/search?page=${page}&size=${size}&search=${search}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.serverItemsLength = object.length
          this.retailers = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
    getConditionText(condition) {
      const conditionMap = {
        MORE_THAN: 'more than',
        LESS_THAN: 'less than',
      }
      return conditionMap[condition] || condition
    },
  },
  computed: {
    periodStr: {
      get() {
        return this.audienceObject.period != null ? String(this.audienceObject.period) : undefined
      },
      set(value) {
        this.audienceObject.period = Number(value)
      },
    },
    isEditing() {
      return this.audience != null
    },
    validForm() {
      return (
        this.audienceObject.name != '' &&
        (this.audienceObject.demographicConditions.length > 0 ||
          this.audienceObject.retailerSegments.length > 0)
      )
    },
    validateDemographics() {
      if (this.audienceObject.demographicConditions.length > 0) {
        for (var i = 0; i < this.audienceObject.demographicConditions.length; i++) {
          var element = this.audienceObject.demographicConditions[i]
          if (element.amount == undefined) {
            return false
          }
        }
      }
      return true
    },
    validateRetailers() {
      if (this.audienceObject.retailerSegments.length > 0) {
        if (this.audienceObject.retailerSegments[0].retailerConditions.length == 0) {
          return false
        }

        for (
          var i = 0;
          i < this.audienceObject.retailerSegments[0].retailerConditions.length;
          i++
        ) {
          var element = this.audienceObject.retailerSegments[0].retailerConditions[i]
          if (element.retailerWebsite != '' && element.amount == undefined) {
            return false
          }
        }
      }
      return true
    },
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getRetailers(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
